// ─── Step compiler ────────────────────────────────────────────────────────────
// Turns a step-based tactic (formation + list of steps) into the keyframes and
// actions that every viewer understands (2D editor playback, /tactic 3D view).
//
// A step is: some players skate to new spots + at most one puck action.
// Timing is computed from distance and skating speed, so the coach never has
// to place anything on a timeline by hand.

// Must match TacticEngine.js / TacticEngine3D (three/constants.js)
export const PASS_DURATION  = 0.5;
export const SHOOT_DURATION = 0.8;

export const SPEEDS = { slow: 3, normal: 5, sprint: 7.5 };   // units per second
export const SPEED_LABELS = { slow: 'Lento', normal: 'Normal', sprint: 'Sprint' };

const MIN_MOVE   = 0.6;    // shortest skate, seconds
const STILL_EPS  = 0.05;   // moves shorter than this are ignored
const PAUSE      = 0.25;   // breathing room after a pass/shot lands
const EMPTY_STEP = 0.6;    // a step with nothing in it still takes a moment
const END_PAD    = 0.5;

const r2 = v => Math.round(v * 100) / 100;

export const isOpponent = p => !!p && p.team === 'opponent';

// Formation = each player's earliest keyframe
export function formationOf(players) {
    return (players || []).map(p => {
        const kfs = p.keyframes || [];
        if (kfs.length === 0) return { x: 0, y: 0 };
        const first = kfs.reduce((a, b) => (b.t < a.t ? b : a));
        return { x: first.x, y: first.y };
    });
}

export function moveDuration(from, to, speed) {
    const dist = Math.hypot(to.x - from.x, to.y - from.y);
    return Math.max(MIN_MOVE, dist / (SPEEDS[speed] || SPEEDS.normal));
}

// A tactic is edited by steps if it has steps, or if nothing has been animated yet
export function isStepTactic(tactic) {
    if (!tactic) return false;
    if (Array.isArray(tactic.steps) && tactic.steps.length > 0) return true;
    const noActions = !tactic.actions || tactic.actions.length === 0;
    const noMotion  = (tactic.players || []).every(p => (p.keyframes || []).length <= 1);
    return noActions && noMotion;
}

/**
 * @returns {{
 *   players: object[],            players with regenerated keyframes
 *   actions: object[],            compiled actions (with `step` index)
 *   duration: number,
 *   stepTimes: {start, end}[],    per step
 *   carrierAt: number[],          puck carrier at the start of each step (+1 entry: after the last step), -1 = nobody
 *   startPositions: {x,y}[][],    every player's position at the start of each step (+1 entry: final)
 *   invalidSteps: number[],       steps whose puck action no longer makes sense (ignored)
 * }}
 */
export function compileSteps(players, steps, puckStart = 0) {
    const formation = formationOf(players);
    const pos = formation.map(p => ({ ...p }));
    const kfs = formation.map(p => [{ t: 0, x: p.x, y: p.y }]);
    const actions = [];
    const stepTimes = [];
    const carrierAt = [];
    const startPositions = [];
    const invalidSteps = [];

    let t = 0;
    // Whoever starts with the puck (the goalie unless the formation says otherwise)
    let carrier = players && players[puckStart] && !isOpponent(players[puckStart]) ? puckStart : (players && players.length > 0 ? 0 : -1);

    (steps || []).forEach((step, si) => {
        carrierAt.push(carrier);
        startPositions.push(pos.map(p => ({ ...p })));
        const start = t;
        let longest = 0;

        (step.moves || []).forEach(m => {
            const i = m.playerIndex;
            if (!players[i]) return;
            const from = pos[i];
            const to = { x: m.x, y: m.y };
            if (Math.hypot(to.x - from.x, to.y - from.y) < STILL_EPS) return;
            const dur = moveDuration(from, to, m.speed);
            kfs[i].push({ t: r2(start), x: from.x, y: from.y });
            kfs[i].push({ t: r2(start + dur), x: to.x, y: to.y });
            pos[i] = to;
            longest = Math.max(longest, dur);
        });

        let end = start + longest;
        const a = step.action;
        if (a) {
            const validPass  = a.type === 'pass' && a.from === carrier && players[a.to] && !isOpponent(players[a.to]) && a.to !== a.from;
            const validShoot = a.type === 'shoot' && a.from === carrier;
            if (validPass || validShoot) {
                const at = a.timing === 'end' ? start + longest : start;
                actions.push({
                    type: validPass ? 'pass' : 'shoot',
                    fromPlayerIndex: a.from,
                    toPlayerIndex: validPass ? a.to : null,
                    t: r2(at),
                    step: si,
                });
                end = Math.max(end, at + (validPass ? PASS_DURATION : SHOOT_DURATION) + PAUSE);
                carrier = validPass ? a.to : -1;
            } else {
                invalidSteps.push(si);
            }
        }
        if (end - start < 1e-6) end = start + EMPTY_STEP;
        stepTimes.push({ start: r2(start), end: r2(end) });
        t = end;
    });
    carrierAt.push(carrier);
    startPositions.push(pos.map(p => ({ ...p })));

    // Sort + merge keyframes that landed on the same time (keep the later write)
    const merged = kfs.map(list => {
        const sorted = list.slice().sort((a, b) => a.t - b.t);
        const out = [];
        sorted.forEach(k => {
            const last = out[out.length - 1];
            if (last && Math.abs(last.t - k.t) < 0.005) out[out.length - 1] = k;
            else out.push(k);
        });
        return out;
    });

    return {
        players: (players || []).map((p, i) => ({ ...p, keyframes: merged[i] })),
        actions,
        duration: Math.max(1, r2(t + END_PAD)),
        stepTimes,
        carrierAt,
        startPositions,
        invalidSteps,
    };
}

// Apply a compile result onto a tactic object
export function withCompiledSteps(tactic, steps) {
    const c = compileSteps(tactic.players, steps, tactic.puckStart ?? 0);
    return {
        ...tactic,
        steps,
        players: c.players,
        actions: c.actions.map(({ step, ...a }) => a),
        duration: c.duration,
    };
}
